﻿<template>
  <el-container class="layout">
    <!-- 顶部栏：系统名 + 四级作用域选择器 + 全局搜索 + 面包屑 -->
    <el-header height="auto" class="topbar">
      <div class="row1">
        <div class="brand">⚡ 动环设备管理系统 <span class="ver">© 2026 qls &amp; TeleAgent</span></div>
        <div class="scope-selector">
          <el-cascader v-model="scopePath" :options="scopeOptions" placeholder="切换作用域：局站 / 系统（默认安吉电信-全部）" clearable filterable style="width:300px" @change="onScopeChange" />
          <el-input v-model="gsKeyword" placeholder="全局搜索：设备/型号/厂家/编号" clearable style="width:280px; margin-left:10px" @keyup.enter="globalSearch">
            <template #append>
              <el-button icon="Search" @click="globalSearch">搜索</el-button>
            </template>
          </el-input>
          <el-tag v-if="scopeStatText" type="info" effect="plain" style="margin-left:8px">{{ scopeStatText }}</el-tag>
        </div>
        <div class="quick">
          <el-button v-if="canWrite()" type="primary" icon="Upload" @click="go('#/import')">Excel 导入</el-button>
          <el-dropdown v-if="userStore.loaded" @command="userCmd" style="margin-left:10px">
            <el-button size="small">
              <span v-if="userStore.user">{{ userStore.user.displayName || userStore.user.username }}
                <el-tag size="small" :type="userStore.user.share ? 'warning' : userStore.user.role === 'admin' ? 'danger' : userStore.user.role === 'editor' ? 'success' : 'info'" style="margin-left:6px">{{ userStore.user.share ? '只读分享' : userStore.user.role === 'admin' ? '管理员' : userStore.user.role === 'editor' ? '编辑员' : '只读' }}</el-tag>
                <span v-if="userStore.user.freeLogin" class="muted" style="font-size:11px">（免登录）</span>
              </span>
              <span v-else class="muted">未登录</span>
              <el-icon style="margin-left:4px"><ArrowDown /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-if="!userStore.user" command="login">登录…</el-dropdown-item>
                <el-dropdown-item v-else command="password">修改密码</el-dropdown-item>
                <el-dropdown-item v-if="isAdmin()" command="toggleLogin" divided>{{ userStore.loginEnabled ? '关闭登录（回到免登录）' : '启用登录（所有访问需账号）' }}</el-dropdown-item>
                <el-dropdown-item v-if="userStore.user && !userStore.user.freeLogin" command="logout" divided>退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
      <el-breadcrumb separator="/" class="crumb">
        <el-breadcrumb-item>安吉电信</el-breadcrumb-item>
        <el-breadcrumb-item v-if="scopeLabels.station">{{ scopeLabels.station }}</el-breadcrumb-item>
        <el-breadcrumb-item v-if="scopeLabels.system">{{ scopeLabels.system }}</el-breadcrumb-item>
        <el-breadcrumb-item v-if="scopeLabels.type">类型={{ scopeLabels.type }}</el-breadcrumb-item>
      </el-breadcrumb>
      <el-alert v-if="hazardBanner && (hazardBanner.overdue > 0 || hazardBanner.soon > 0)" type="warning" :closable="false" show-icon class="hz-banner">
        <span style="cursor:pointer" @click="go('#/hazards?overdue=1')">⚠ 隐患提醒：{{ hazardBanner.overdue }} 条逾期 / {{ hazardBanner.soon }} 条即将到期（点击查看台账）</span>
      </el-alert>
    </el-header>

    <el-container>
      <!-- 左侧菜单 -->
      <el-aside width="210px" class="menu">
      <el-menu :default-active="active" @select="onMenu">
          <el-menu-item index="#/import"><el-icon><Upload /></el-icon> <span>导入向导</span> <el-tag size="small" style="margin-left:4px" effect="dark">日常主入口</el-tag></el-menu-item>
          <el-menu-item index="#/"><el-icon><Odometer /></el-icon> <span>统计看板</span></el-menu-item>
          <el-menu-item index="#/equipment"><el-icon><Coin /></el-icon> <span>设备台账</span></el-menu-item>
          <el-menu-item index="#/connections"><el-icon><Share /></el-icon> <span>连接台账</span></el-menu-item>
          <el-menu-item index="#/systems"><el-icon><Grid /></el-icon> <span>系统视图</span></el-menu-item>
          <el-menu-item index="#/knowledge"><el-icon><Collection /></el-icon> <span>知识库</span></el-menu-item>
          <el-menu-item index="#/hazards"><el-icon><Warning /></el-icon> <span>安全隐患</span></el-menu-item>
          <el-menu-item index="#/topology"><el-icon><Connection /></el-icon> <span>拓扑图</span></el-menu-item>
          <el-menu-item index="#/report"><el-icon><Document /></el-icon> <span>报表导出</span></el-menu-item>
          <el-menu-item index="#/admin"><el-icon><Setting /></el-icon> <span>系统管理</span></el-menu-item>
        </el-menu>
      </el-aside>

      <el-main class="main">
        <component :is="viewComp" :key="viewKey" />
      </el-main>
    </el-container>
  </el-container>
</template>

<script setup>
import { ref, computed, onMounted, defineAsyncComponent, watch } from 'vue';
import {  api, scopeStore, userStore, canWrite, isAdmin, fetchMe  } from './store.js';
import { ElMessage, ElMessageBox } from 'element-plus';

const viewComp = ref(null);
const viewKey = ref(0);
const route = ref(location.hash || '#/');
const scopePath = ref([]);
const scopeOptions = ref([]);
const stat = ref({});
const active = computed(() => route.value.split('?')[0] || '#/');

const views = {
  '#/import': () => import('./views/Import.vue'),
  '#/': () => import('./views/Dashboard.vue'),
  '#/equipment': () => import('./views/EquipmentList.vue'),
  '#/equipment-detail': () => import('./views/EquipmentDetail.vue'),
  '#/connections': () => import('./views/ConnectionList.vue'),
  '#/systems': () => import('./views/SystemView.vue'),
  '#/knowledge': () => import('./views/Knowledge.vue'),
  '#/hazards': () => import('./views/Hazard.vue'),
  '#/hazard-detail': () => import('./views/HazardDetail.vue'),
  '#/topology': () => import('./views/Topology.vue'),
  '#/report': () => import('./views/Report.vue'),
  '#/admin': () => import('./views/Admin.vue'),
  '#/login': () => import('./views/Login.vue'),
  '#/share': () => import('./views/Share.vue')
};

async function render() {
  route.value = location.hash || '#/';
  const base = route.value.split('?')[0] || '#/';
  // 启用登录后未登录：除登录/分享页外一律跳登录（前端拦截；后端 API 已独立防护）
  if (userStore.loginEnabled && !userStore.user && base !== '#/login' && base !== '#/share') {
    location.hash = `#/login?back=${encodeURIComponent(route.value)}`;
    return;
  }
  const loader = views[base] || views['#/'];
  viewComp.value = defineAsyncComponent(loader);
  viewKey.value++;
  if (scopeStore.tree) refreshStat();
}

async function userCmd(cmd) {
  if (cmd === 'login') { location.hash = `#/login?back=${encodeURIComponent(location.hash || '#/')}&force=1`; return; }
  if (cmd === 'logout') {
    await api.post('/api/auth/logout');
    await fetchMe();
    ElMessage.success('已退出登录');
    setTimeout(() => location.reload(), 200);
    return;
  }
  if (cmd === 'password') {
    // 免登录管理员：为 admin 账号设置密码；已登录：验旧改新
    const free = userStore.user && userStore.user.freeLogin;
    try {
      const { value: v } = await ElMessageBox.prompt(free ? '为 admin 账号设置新密码（至少6位，设置后可用其登录）' : '输入新密码（至少6位）', '修改密码', { inputType: 'password', inputPattern: /^.{6,}$/, inputErrorMessage: '至少 6 位' });
      let oldP = '';
      if (!free) { const r2 = await ElMessageBox.prompt('输入原密码验证', '修改密码', { inputType: 'password' }); oldP = r2.value; }
      await api.put('/api/auth/password', { oldPassword: oldP, newPassword: v });
      ElMessage.success(free ? 'admin 密码已设置' : '密码已修改');
    } catch (e) { if (e && e.message) ElMessage.error(e.message); }
    return;
  }
  if (cmd === 'toggleLogin') {
    const on = !userStore.loginEnabled;
    try {
      if (on) { await ElMessageBox.confirm('启用后所有访问都需账号登录（建议先创建好编辑员/只读账号并修改 admin 初始密码）。确认启用？', '启用登录', { type: 'warning' }); }
      await api.put('/api/app-config/login-enabled', { enabled: on });
      ElMessage.success(on ? '已启用登录：请重新登录' : '已回到免登录模式');
      await fetchMe();
      setTimeout(() => location.reload(), 400);
    } catch (e) { if (e && e.message && e !== 'cancel') ElMessage.error(e.message); }
  }
}

function go(hash) { if (location.hash === hash) render(); else location.hash = hash; }
function onMenu(i) { location.hash = i; }

// ---------- 全局搜索（顶栏输入框：回车 / 搜索按钮均可触发，跳转设备台账带关键词） ----------
const gsKeyword = ref('');
function globalSearch() {
  const kw = (gsKeyword.value || '').trim();
  if (!kw) { ElMessage.warning('请输入搜索关键词（设备名/型号/厂家/资产编号）'); return; }
  go(`#/equipment?keyword=${encodeURIComponent(kw)}`);
}

// ---------- 登录横幅：隐患逾期/临期提醒（Q37：本期界面提醒） ----------
const hazardBanner = ref(null);
async function refreshHazardBanner() {
  try {
    const r = await api.get('/api/hazards/stats');
    hazardBanner.value = r.banner || null;
  } catch { hazardBanner.value = null; }
}

window.addEventListener('hashchange', render);

// ---------- 作用域 ----------
async function loadScopeTree() {
  const t = await api.get('/api/scope/tree');
  scopeStore.tree = t;
  // 组装级联选项：组织 → 局站 → (系统 | 系统类型标记)
  const opts = [];
  for (const org of t.organizations) {
    const o = { value: `o-${org.id}`, label: org.name, children: [] };
    for (const st of t.stations.filter(s => s.organization_id === org.id)) {
      const s = { value: `s-${st.id}`, label: st.name, children: [
        { value: 'all-systems', label: '全部系统' },
        ...t.systems.filter(x => x.station_id === st.id).map(x => ({ value: `sys-${x.id}`, label: x.name + (x.type ? `（${x.type}）` : '') })),
        { value: 'divider', label: '── 按系统类型 ──', disabled: true },
        ...t.systemTypes.map(ty => ({ value: `ty-${ty}`, label: `类型：${ty}` }))
      ] };
      o.children.push(s);
    }
    opts.push(o);
  }
  scopeOptions.value = opts;
}

function onScopeChange(val) {
  scopeStore.organization_id = null; scopeStore.station_id = null;
  scopeStore.system_id = null; scopeStore.system_type = null; scopeStore.equipment_id = null;
  scopePath.value = val || [];
  const v = (val || []).slice(-1)[0];
  if (!v) { refreshStat(); return; }
  const [kind, id] = [v.split('-')[0], v.split('-').slice(1).join('-')];
  // 沿路径确定组织与局站
  const path = val || [];
  for (const p of path) {
    if (p.startsWith('o-')) scopeStore.organization_id = +p.slice(2);
    if (p.startsWith('s-')) scopeStore.station_id = +p.slice(2);
  }
  if (kind === 'sys') scopeStore.system_id = +id;
  if (kind === 'ty') scopeStore.system_type = id;
  if (scopeStore.station_id || scopeStore.system_id || scopeStore.system_type) scopeStore.organization_id = scopeStore.organization_id || null;
  refreshStat();
}

const scopeLabels = computed(() => {
  const t = scopeStore.tree; if (!t) return {};
  const out = {};
  if (scopeStore.station_id) out.station = (t.stations.find(s => s.id === scopeStore.station_id) || {}).name || '';
  if (scopeStore.system_id) { const s = t.systems.find(x => x.id === scopeStore.system_id); out.system = s ? s.name : ''; }
  if (scopeStore.system_type) out.type = scopeStore.system_type;
  return out;
});

const scopeStatText = computed(() => {
  if (!stat.value || stat.value.equipment === undefined) return '';
  return `设备 ${stat.value.equipment} · 连接 ${stat.value.connections} · 待补连接 ${stat.value.orphan}`;
});

async function refreshStat() {
  try {
    stat.value = await api.get('/api/dashboard', {
      organization_id: scopeStore.organization_id || '',
      station_id: scopeStore.station_id || '',
      system_id: scopeStore.system_id || '',
      system_type: scopeStore.system_type || ''
    });
  } catch (e) { stat.value = {}; }
}

onMounted(async () => {
  // 修复（2026-10-16）：启用登录后匿名访问（#/login、#/share 直开）时 /api/scope/tree 返回 401，
  // 异常冒泡导致 render() 永不执行、主区空白。作用域树加载失败可容忍（登录后 reload 会重拉）。
  try { await fetchMe(); } catch { /* 身份接口异常不阻塞渲染 */ }
  try { await loadScopeTree(); } catch { /* 未登录（登录模式）拿不到作用域树，正常：登录后整页重载 */ }
  refreshHazardBanner();
  render();
});
watch(() => scopeStore, () => {}, { deep: true });
</script>

<style>
html, body, #app { height: 100%; margin: 0; }
.layout { height: 100%; }
.topbar { background: #fff; border-bottom: 1px solid #e4e7ed; padding: 8px 16px 4px; }
.topbar .row1 { display: flex; align-items: center; gap: 16px; }
.brand { font-weight: 700; font-size: 15px; color: #1f2d3d; white-space: nowrap; }
.ver { font-weight: 400; font-size: 11px; color: #909399; margin-left: 6px; }
.scope-selector { flex: 1; display: flex; align-items: center; }
.crumb { margin-top: 4px; }
.menu { border-right: 1px solid #e4e7ed; background: #fafbfc; }
.menu .el-menu { border-right: none; background: transparent; }
.main { background: #f5f7fa; padding: 12px; overflow: auto; }
.pane { background: #fff; border-radius: 6px; padding: 14px; box-shadow: 0 1px 3px rgba(0,0,0,.06); margin-bottom: 12px; }
.pane h3 { margin: 0 0 10px; font-size: 15px; color: #303133; }
.muted { color: #909399; font-size: 12px; }
.tagline { font-size: 12px; }
.hz-banner { margin-top: 6px; padding: 4px 12px; }
</style>

